import { useEffect, useState } from "react";
import { fetchProjectMentions, type ConsoleSite } from "../api";
import { matchAskMentions, type AskMention } from "../../../../backend/src/shared/askMentions";

/** A request is scoped to both workspace and project selection; stale responses
 * cannot reintroduce another workspace's people into the menu. */
export function useAskMentions(sites: ConsoleSite[], query: string | null, search: string, knownCompanies: Array<string | undefined> = []) {
  const project = new URLSearchParams(search).get("site");
  const siteIds = sites.filter(site => site.syncSource && (!project || site.id === project)).map(site => site.id).sort();
  const key = JSON.stringify([search, siteIds, query]);
  const [result, setResult] = useState<{ key: string; choices: AskMention[]; failed: boolean } | null>(null);
  useEffect(() => {
    if (query === null) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        const choices: AskMention[] = [];
        let failed = false;
        let next = 0;
        // Bound provider fan-out for workspaces with many projects.
        await Promise.all(Array.from({ length: Math.min(4, siteIds.length) }, async () => {
          while (!cancelled && next < siteIds.length) {
            const id = siteIds[next++]!;
            try { choices.push(...await fetchProjectMentions(id, query, search)); }
            catch { failed = true; }
          }
        }));
        if (!cancelled) setResult({ key, choices, failed });
      })();
    }, 180);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [key]);
  const current = result?.key === key ? result : null;
  const directoryCompanies = new Set(current?.choices.filter(choice => choice.kind === "company").map(choice => choice.name.trim().toLocaleLowerCase()));
  const localCompanies = knownCompanies.map(name => name?.trim()).filter((name): name is string => !!name && !directoryCompanies.has(name.toLocaleLowerCase()));
  return {
    choices: matchAskMentions([
      ...sites.map(site => ({ id: site.id, name: site.name || site.id, kind: "project" as const })),
      ...localCompanies.map(name => ({ id: `local:${name.toLocaleLowerCase()}`, name, kind: "company" as const })),
      ...(current?.choices ?? []),
    ], query ?? ""),
    loading: query !== null && siteIds.length > 0 && !current,
    failed: current?.failed ?? false,
  };
}
